import { reloadAppAsync } from 'expo';
import { classifySyncDiagnosticFailure } from './classifySyncDiagnosticFailure';
import { hasSyncExecution } from '@/services/sync/SyncExecutionLease';
import { SyncFailureJournal, SYNC_DIAGNOSTIC_TTL_MS } from './SyncFailureJournal';
import { registerSyncDiagnosticListener } from './syncDiagnosticEvents';
import { mergeSyncDiagnosticEvents } from './mergeSyncDiagnosticEvents';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { v4 as uuidv4 } from 'uuid';
import { globalState } from '@/state/globalState';
import { supabase } from '@/services/supabase';
import { databaseService } from '@/services/databaseService';
import { getRuntimeClientBuild } from '@/services/runtime/clientBuild';
import { getStableRecoveryDeviceId, recoveryRuntimeGate } from '@/services/recovery/RecoveryRuntimeGate';
import { getLastSyncTimestamp, registerSupportDiagnosticSyncHook, saveLastSyncTimestamp, syncAll } from '@/services/syncService';
import { bindBufferedLogIdentity, getBufferedLogEvents, getSyncEssentialEvents } from '@/utils/logBuffer';
import { SupportDiagnosticBuffer, SUPPORT_DIAGNOSTIC_MAX_BYTES } from './SupportDiagnosticBuffer';
import { bindMediaDiagnosticIdentity, appendMediaDiagnosticEvents, appendMediaAttemptDiagnosticEvents } from './mediaDiagnosticRuntime';
import { recoverMediaAttempts, acknowledgeMediaAttempts, registerMediaFailureListener } from './mediaAttemptRuntime';
import { MMKVSupportDiagnosticPreparedStore } from './MMKVSupportDiagnosticPreparedStore';
import { SupportDiagnosticService } from './SupportDiagnosticService';
import { SUPPORT_DIAGNOSTICS_BUCKET, SupabaseSupportDiagnosticTransport, type SupportDiagnosticUploadScope } from './SupabaseSupportDiagnosticTransport';
import { enqueueLegacySupportCheck } from '@/services/runtime/supportCollectionQueue';
import { hashSupportSyncSummary } from './supportSyncSummary';

const PROTOCOL = 1;
type Scope = { companyId: string; userId: string; deviceId: string; clientBuild: number };
const first = <T>(data: T | T[] | null): T | null => Array.isArray(data) ? (data[0] ?? null) : data;
const timeout = <T>(promise: PromiseLike<T>, ms = 8_000): Promise<T> => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('support_timeout')), ms);
  Promise.resolve(promise).then(
    value => { clearTimeout(timer); resolve(value); },
    error => { clearTimeout(timer); reject(error); }
  );
});

let identityKey: string | null = null;
let administrativePreparedStore: MMKVSupportDiagnosticPreparedStore | null = null;
const getAdministrativePreparedStore = (): MMKVSupportDiagnosticPreparedStore => {
  administrativePreparedStore ??= new MMKVSupportDiagnosticPreparedStore();
  return administrativePreparedStore;
};
export const bindSupportDiagnosticIdentity = (companyId?: string | null, userId?: string | null): void => {
  bindMediaDiagnosticIdentity(companyId, userId);
  const next = companyId && userId ? `${companyId}:${userId}` : null;
  bindBufferedLogIdentity(next);
  if (next !== identityKey) {
    administrativePreparedStore?.clear();
  }
  identityKey = next;
};

const requireScope = async (): Promise<Scope> => {
  if (!globalState.network.isConnected.get()) throw new Error('support_offline');
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const clientBuild = getRuntimeClientBuild();
  if (!user?.id || profile?.id !== user.id || !profile.empresa_id || company?.id !== profile.empresa_id || clientBuild <= 0) throw new Error('support_identity_incomplete');
  const { data, error } = await timeout(supabase.auth.getUser());
  if (error || data.user?.id !== user.id) throw new Error('support_session_divergent');
  bindSupportDiagnosticIdentity(profile.empresa_id, user.id);
  return { companyId: profile.empresa_id, userId: user.id, deviceId: getStableRecoveryDeviceId(), clientBuild };
};

const ensureSupportScopeUnchanged = async (expected: Scope, stage: 'collection' | 'send'): Promise<void> => {
  const current = await requireScope();
  if (current.companyId !== expected.companyId || current.userId !== expected.userId ||
      current.deviceId !== expected.deviceId || current.clientBuild !== expected.clientBuild) {
    throw new Error(`support_session_changed_${stage}`);
  }
};

const countBy = (values: unknown[], pick: (value: any) => string): Record<string, number> => values.reduce<Record<string, number>>((result, value) => {
  const raw = pick(value).toLowerCase();
  const key = /^(create|update|delete|checklist_partial_update|checklist_delete|pending|failed|resolved|retry|ignored|open|blocked|tombstoned_entity|field_retired|unknown_field|snapshot_mismatch|conflict_materialized|unknown|scope_[a-z0-9_]{1,80})$/.test(raw) ? raw : 'other';
  result[key] = (result[key] ?? 0) + 1;
  return result;
}, {});

const collectSummary = async (strictResetScope = false) => {
  if (!globalState.database.isInitialized.get()) throw new Error('support_database_unavailable');
  const scope = await requireScope();
  const { operations, images, deletions, conflicts: activeConflicts, conflictEffects, conflictSummaryRows, scopeCountsByTable } =
    await (strictResetScope
      ? databaseService.getSupportResetScopeRows(scope.companyId, scope.userId)
      : databaseService.getSupportSynchronizationSummaryRows(scope.companyId, scope.userId));
  await ensureSupportScopeUnchanged(scope, 'collection');
  const cleanup = await databaseService.listItems<any>('deferred_media_cleanup');
  await ensureSupportScopeUnchanged(scope, 'collection');
  const conflicts = conflictSummaryRows ?? activeConflicts;
  const now = Date.now();
  const timestamps = operations.map(operation => Number(operation.timestamp)).filter(Number.isFinite).map(value => value < 1e12 ? value * 1000 : value);
  const lastSync = getLastSyncTimestamp({ companyId: scope.companyId, userId: scope.userId });
  const scopeCountRows = Object.entries(scopeCountsByTable ?? {}).flatMap(([table, reasons]) =>
    Object.entries(reasons ?? {}).filter(([, count]) => Number(count) > 0).map(([reason, count]) => ({
      key: `scope_${table}_${reason}`,
      cautionCode: `support_scope_${table}_${reason}`,
      count: Number(count),
    }))
  );
  const pendingByType = countBy(operations, value => String(value.operation ?? 'other'));
  scopeCountRows.forEach(({ key, count }) => { pendingByType[key] = (pendingByType[key] ?? 0) + count; });
  const cautionCodes = [
    operations.length && 'pending_operations', images.length && 'pending_images',
    deletions.length && 'pending_deletions', conflicts.length && 'sync_conflicts',
    cleanup.some(row => row.empresa_id === scope.companyId && row.user_id === scope.userId) && 'deferred_media_cleanup',
    ...scopeCountRows.map(row => row.cautionCode),
  ].filter(Boolean) as string[];
  return { scope, summary: {
    pending_by_type: pendingByType,
    pending_images: images.length, pending_deletions: deletions.length,
    conflicts_by_reason: countBy(conflicts, value => String(value.reason ?? 'unknown')),
    conflicts_by_effect: conflictEffects ?? (conflicts.length ? { needs_review: conflicts.length } : {}),
    conflicts_by_status: countBy(conflicts, value => String(value.status ?? 'other')),
    oldest_pending_age_seconds: timestamps.length ? Math.max(0, Math.floor((now - Math.min(...timestamps)) / 1000)) : null,
    last_sync_age_seconds: lastSync ? Math.max(0, Math.floor((now - lastSync) / 1000)) : null,
    sync_status: globalState.sync.lastStatus.get(), network_connected: true, database_initialized: true,
    recovery_gate: recoveryRuntimeGate.getSnapshot().status, tombstone_gate: recoveryRuntimeGate.getSnapshot().blocked,
    caution_codes: cautionCodes, platform: Platform.OS,
  }};
};

/**
 * Monta o mesmo resumo usado pelo botão manual, sem iniciar uma sincronização.
 * O hash remoto de SYNC permanece SHA-512 em Base64 (full-state usa hex).
 */
export const collectSupportSynchronizationSummary = async (): Promise<{
  scope: Scope;
  summary: Record<string, unknown>;
  payloadSha512: string;
}> => {
  const { scope, summary } = await collectSummary();
  return {
    scope,
    summary: summary as Record<string, unknown>,
    payloadSha512: hashSupportSyncSummary(summary),
  };
};

export const verifySupportSynchronization = async (): Promise<{ protocol: string }> => {
  const { scope, summary, payloadSha512 } = await collectSupportSynchronizationSummary();
  await ensureSupportScopeUnchanged(scope, 'send');
  const { data, error } = await timeout(supabase.rpc('app_guincho_submit_sync_verification_v1', {
    p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild,
    p_idempotency_key: uuidv4(), p_payload_sha512: payloadSha512, p_summary: summary,
  }));
  const result = first(data as { protocol: string }[] | null);
  if (error || !result?.protocol) throw error ?? new Error('support_verification_failed');
  return result;
};

export const authorizeSupportReset = async () => {
  if (recoveryRuntimeGate.getSnapshot().blocked) throw new Error('support_recovery_blocked');
  const { scope, summary } = await collectSummary(true);
  await ensureSupportScopeUnchanged(scope, 'send');
  const { data, error } = await timeout(supabase.rpc('app_guincho_authorize_support_reset_v1', { p_client_build: scope.clientBuild }));
  const authorization = first(data as { empresa_id: string; user_id: string; checked_at: string }[] | null);
  if (error || !authorization || authorization.empresa_id !== scope.companyId || authorization.user_id !== scope.userId) throw error ?? new Error('support_reset_forbidden');
  return { scope, summary, checkedAt: authorization.checked_at };
};

export const performControlledSupportReset = async (): Promise<void> => {
  const { scope, checkedAt } = await authorizeSupportReset();
  // O próximo processo precisa fazer carga completa; callbacks do processo antigo ficam invalidados.
  saveLastSyncTimestamp(0, { companyId: scope.companyId, userId: scope.userId });
  if (!await databaseService.clearAllDataForSupportReset({ companyId: scope.companyId, userId: scope.userId, checkedAt })) throw new Error('support_reset_clear_failed');
  try { await reloadAppAsync('support-reset'); } catch { /* O usuário pode reabrir manualmente. */ }
  throw new Error('database_reset_restart_required');
};

const storageClient = {
  async getUserId() { const { data, error } = await supabase.auth.getUser(); if (error) throw error; return data.user?.id ?? ''; },
  async upload(input: { bucket: string; path: string; body: Uint8Array; contentType: string; metadata: Record<string, string | number> }) {
    const { data, error } = await supabase.storage.from(input.bucket).upload(input.path, input.body, { contentType: input.contentType, upsert: false, metadata: input.metadata });
    if (error) {
      const status = String((error as any).statusCode ?? (error as any).status ?? '');
      const code = String((error as any).error ?? (error as any).code ?? '');
      if (status !== '409' && code.toLowerCase() !== 'duplicate') throw error;
      return { path: input.path };
    }
    return { path: data.path };
  },
  async finalize(input: SupportDiagnosticUploadScope & { diagnosticId: string; path: string; sha512: string; byteLength: number }) {
    const { data, error } = await supabase.rpc('app_guincho_finalize_support_diagnostic_v1', {
      p_diagnostic_id: input.diagnosticId, p_empresa_id: input.companyId, p_device_id: input.deviceId,
      p_storage_path: input.path, p_sha512: input.sha512, p_size_bytes: input.byteLength,
      p_event_count: input.eventCount, p_client_build: input.clientBuild, p_kind: input.kind, p_request_id: input.requestId,
    });
    const result = first(data as { protocol: string }[] | null); if (error || !result?.protocol) throw error ?? new Error('support_finalize_failed'); return result.protocol;
  },
};

const getPostUpdatePreparedStore = (scope: Scope, requestId: string): MMKVSupportDiagnosticPreparedStore =>
  new MMKVSupportDiagnosticPreparedStore(new MMKV({
    id: `app-guincho-post-update-diagnostic:${scope.companyId}:${scope.userId}:${scope.deviceId}:${requestId}`,
  }));

const sendForRequest = async (scope: Scope, requestId: string, kind: 'manual' | 'remote_request' | 'post_update') => {
  const buffer = new SupportDiagnosticBuffer({ maxBytes: SUPPORT_DIAGNOSTIC_MAX_BYTES - 512 * 1024 });
  getBufferedLogEvents().forEach(event => buffer.record({ occurredAt: new Date(event.timestamp).toISOString(), level: event.level === 'custom' || event.level === 'log' ? 'info' : event.level, category: event.tag, name: 'buffered_log', message: event.message, data: event.data ?? null }));
  appendMediaDiagnosticEvents(buffer, scope.companyId, scope.userId);
  const transportKind: SupportDiagnosticUploadScope['kind'] = kind === 'post_update' ? 'remote_request' : kind;
  const uploadScope = { ...scope, requestId, kind: transportKind, eventCount: buffer.getStats().eventCount };
  const store = kind === 'remote_request'
    ? getAdministrativePreparedStore()
    : kind === 'post_update'
      ? getPostUpdatePreparedStore(scope, requestId)
      : undefined;
  const service = new SupportDiagnosticService({ buffer, transport: new SupabaseSupportDiagnosticTransport(storageClient, uploadScope), createId: () => requestId, store });
  const metadata = { appVersion: Constants.expoConfig?.version ?? 'unknown', clientBuild: scope.clientBuild, platform: Platform.OS, requestKind: transportKind, protocol: PROTOCOL, bucket: SUPPORT_DIAGNOSTICS_BUCKET };
  if (kind === 'remote_request' || kind === 'post_update') {
    let prepared;
    try {
      prepared = await service.loadLatestPrepared();
    } catch {
      store!.clear();
      prepared = null;
    }
    if (prepared?.diagnosticId !== requestId || prepared.administrativeRequestId !== requestId) {
      store!.clear();
      prepared = await service.prepareForAdministrativeRequest({ requestId, metadata });
    }
    const receipt = await service.sendAdministrative(prepared.diagnosticId, { administrativeRequestId: requestId });
    // O coordenador só descarta o payload do lote depois de confirmar o
    // registro da etapa. Uma queda entre finalize e esse registro precisa
    // reenviar exatamente o mesmo hash sob o mesmo request UUID.
    if (kind === 'remote_request') await service.discardPrepared(prepared.diagnosticId);
    return receipt;
  }
  const consent = { granted: true as const, grantedAt: new Date().toISOString(), purpose: 'support_diagnostic' as const };
  const prepared = await service.prepareForExplicitSend({ consent, metadata });
  return service.sendExplicit(prepared.diagnosticId, { explicitUserAction: true, consent });
};

/** Transporte existente de diagnóstico com o request UUID reservado pelo lote. */
export const sendSupportDiagnosticForPostUpdateRequest = async (requestId: string) => {
  const scope = await requireScope();
  return sendForRequest(scope, requestId, 'post_update');
};

export const clearSupportDiagnosticPostUpdatePrepared = async (requestId: string): Promise<void> => {
  const scope = await requireScope();
  await getPostUpdatePreparedStore(scope, requestId).remove(requestId);
};

export const sendSupportDiagnosticExplicitly = async () => {
  const scope = await requireScope();
  const { data, error } = await timeout(supabase.rpc('app_guincho_prepare_manual_support_diagnostic_v1', { p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild, p_protocol: PROTOCOL }));
  const ticket = first(data as { request_id: string }[] | null);
  if (error || !ticket?.request_id) throw error ?? new Error('support_ticket_failed');
  return sendForRequest(scope, ticket.request_id, 'manual');
};

const mediaDiagnosticStorage = (companyId: string, userId: string) => new MMKV({ id: `app-guincho-auto-diagnostic:${companyId}:${userId}` });
const MEDIA_FAILURE_KEY = 'media_failure_snapshot_v1';
const readMediaFailure = (storage: MMKV) => {
  try {
    const snapshot = JSON.parse(storage.getString(MEDIA_FAILURE_KEY) ?? 'null') as import('@/types/supportDiagnostic').SupportDiagnosticSnapshot | null;
    if (snapshot && Date.now() - Date.parse(snapshot.createdAt) < 7 * 86400000 && Array.isArray(snapshot.events)) return snapshot;
  } catch { /* Captura inválida não impede a próxima ocorrência. */ }
  storage.delete(MEDIA_FAILURE_KEY);
  return null;
};
const appendCircularLogs = (buffer: SupportDiagnosticBuffer) => getBufferedLogEvents().forEach(event => buffer.record({
  occurredAt: new Date(event.timestamp).toISOString(), level: event.level === 'custom' || event.level === 'log' ? 'info' : event.level,
  category: event.tag, name: 'buffered_log', message: event.message, data: event.data ?? null,
}));

registerMediaFailureListener(failure => {
  if (identityKey !== failure.identity) return;
  const [companyId, userId] = failure.identity.split(':');
  const buffer = new SupportDiagnosticBuffer({ maxBytes: 1024 * 1024 });
  appendCircularLogs(buffer);
  appendMediaDiagnosticEvents(buffer, companyId, userId);
  buffer.record({ level: 'error', category: 'checklist_media', name: 'failure_captured', data: {
    attemptId: failure.attemptId, phase: failure.phase ?? null, errorCode: failure.errorCode ?? null,
  } });
  const snapshot = buffer.createImmutableSnapshot({ diagnosticId: uuidv4(), metadata: {
    companyId, userId, deviceId: getStableRecoveryDeviceId(), attemptId: failure.attemptId,
  } });
  const capture = () => {
    if (identityKey !== failure.identity) return;
    const storage = mediaDiagnosticStorage(companyId, userId);
    if (!readMediaFailure(storage)) {
      storage.set(MEDIA_FAILURE_KEY, JSON.stringify(snapshot));
    }
  };
  if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.locks) {
    void navigator.locks.request(`app-guincho-auto-send:${failure.identity}`, capture).catch(() => undefined);
  } else capture();
});

const sendAutomaticMediaDiagnostic = async (scope: Scope): Promise<void> => {
  if (Platform.OS !== 'web') return sendAutomaticMediaDiagnosticLocked(scope);
  // Abas compartilham o pacote persistido; uma unica aba prepara/envia por vez.
  if (typeof navigator === 'undefined' || !navigator.locks) return;
  await navigator.locks.request(`app-guincho-auto-send:${scope.companyId}:${scope.userId}`, { ifAvailable: true }, async lock => {
    if (lock) await sendAutomaticMediaDiagnosticLocked(scope);
  });
};

const sendAutomaticMediaDiagnosticLocked = async (scope: Scope): Promise<void> => {
  const attempts = await recoverMediaAttempts(scope.companyId, scope.userId);
  const storage = mediaDiagnosticStorage(scope.companyId, scope.userId);
  const incident = readMediaFailure(storage);
  if (incident && ((incident.metadata as any).companyId !== scope.companyId || (incident.metadata as any).userId !== scope.userId || (incident.metadata as any).deviceId !== scope.deviceId)) { storage.delete(MEDIA_FAILURE_KEY); return; }
  const store = new MMKVSupportDiagnosticPreparedStore(storage);
  const buffer = new SupportDiagnosticBuffer({ maxBytes: SUPPORT_DIAGNOSTIC_MAX_BYTES - 512 * 1024, previousStats: incident?.stats });
  let prepared;
  try { prepared = await store.loadLatest(); } catch { store.clear(); }
  if (prepared && Date.now() - Date.parse(prepared.createdAt) >= 7 * 24 * 60 * 60 * 1000) {
    // O ticket remoto expira em sete dias; nao repetir indefinidamente um pacote expirado.
    store.clear();
    prepared = null;
  }
  if (!prepared) {
    if ((!attempts.length && !incident) || Date.now() - Number(storage.getString('last_sent_at') ?? 0) < 24 * 60 * 60 * 1000) return;
    if (incident) incident.events.forEach(event => buffer.record(event));
    else { appendCircularLogs(buffer); appendMediaDiagnosticEvents(buffer, scope.companyId, scope.userId); }
    appendMediaAttemptDiagnosticEvents(buffer, scope.companyId, scope.userId, attempts);
    const preparer = new SupportDiagnosticService({ buffer, store, ...(incident ? { createId: () => incident.diagnosticId } : {}), transport: { uploadExplicit: async () => { throw new Error('prepare_only'); } } });
    prepared = await preparer.prepareForSystemAutomatic({ metadata: {
      companyId: scope.companyId, userId: scope.userId, deviceId: scope.deviceId,
      attemptIds: [...new Set([...attempts.map(attempt => attempt.id), ...((incident?.metadata as any)?.attemptId ? [(incident!.metadata as any).attemptId] : [])])], clientBuild: scope.clientBuild, platform: Platform.OS,
      firstOccurrenceAt: incident?.createdAt ?? new Date(Math.min(...attempts.map(attempt => attempt.at))).toISOString(),
      lastOccurrenceAt: incident?.createdAt ?? new Date(Math.max(...attempts.map(attempt => attempt.at))).toISOString(),
      occurrenceCount: Math.max(1, attempts.length),
    } });
  }
  const metadata = (prepared.snapshot.metadata as any)?.context;
  if ((prepared.snapshot.metadata as any)?.delivery_basis?.kind !== 'system_automatic' ||
      metadata?.companyId !== scope.companyId || metadata?.userId !== scope.userId || metadata?.deviceId !== scope.deviceId) {
    store.clear();
    return;
  }
  // Ticket idempotente: repetir apos erro ou reinicio envia exatamente o pacote ja preparado.
  const { data, error } = await timeout(supabase.rpc('app_guincho_prepare_automatic_support_diagnostic_v1', {
    p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild,
    p_request_id: prepared.diagnosticId, p_protocol: PROTOCOL,
    p_context: { first_occurrence_at: metadata.firstOccurrenceAt, last_occurrence_at: metadata.lastOccurrenceAt,
      occurrence_count: metadata.occurrenceCount, platform: metadata.platform },
  }));
  const ticket = first(data as { request_id: string }[] | null);
  if (error || ticket?.request_id !== prepared.diagnosticId) throw error ?? new Error('automatic_diagnostic_ticket_failed');
  if (identityKey !== `${scope.companyId}:${scope.userId}`) return;
  const service = new SupportDiagnosticService({ buffer, store, transport: new SupabaseSupportDiagnosticTransport(storageClient, {
    ...scope, clientBuild: metadata.clientBuild, requestId: prepared.diagnosticId,
    kind: 'system_automatic', eventCount: prepared.snapshot.events.length,
  }) });
  await service.sendSystemAutomatic(prepared.diagnosticId);
  storage.set('last_sent_at', String(Date.now()));
  if (readMediaFailure(storage)?.diagnosticId === prepared.diagnosticId) storage.delete(MEDIA_FAILURE_KEY);
  acknowledgeMediaAttempts(scope.companyId, scope.userId, metadata.attemptIds ?? []);
  await service.discardPrepared(prepared.diagnosticId);
};

let checker: Promise<void> | null = null;
let nextCheckAt = 0;
export const checkPendingSupportDiagnosticRequests = (_trigger: string): Promise<void> => {
  if (checker) return checker;
  if (Date.now() < nextCheckAt || recoveryRuntimeGate.getSnapshot().blocked) return Promise.resolve();
  checker = (async () => {
    try {
      const scope = await requireScope();
      if (!hasSyncExecution() && globalState.database.isInitialized.get()) {
        try {
          const { resumeChecklistMediaPreparations } = require('@/services/checklist/ChecklistImageService') as typeof import('@/services/checklist/ChecklistImageService');
          await resumeChecklistMediaPreparations(scope.companyId, scope.userId);
        } catch { /* A captura preservada poderá ser retomada no próximo ciclo. */ }
      }
      // Falha no automatico nao pode impedir pedidos administrativos existentes.
      try { await sendAutomaticMediaDiagnostic(scope); } catch { /* Repetir no proximo ciclo de suporte. */ }
      try { await sendAutomaticSyncDiagnostic(scope); } catch { /* Pacote de sync continua persistido para reconexão. */ }
      const { data, error } = await timeout(supabase.rpc('app_guincho_claim_support_diagnostic_request_v1', { p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild, p_protocol: PROTOCOL }));
      if (error) throw error;
      const request = first(data as { request_id: string }[] | null);
      if (request?.request_id) {
        try {
          await sendForRequest(scope, request.request_id, 'remote_request');
        } catch (uploadError) {
          await supabase.rpc('app_guincho_fail_support_diagnostic_request_v1', { p_request_id: request.request_id, p_device_id: scope.deviceId, p_error_code: 'upload_failed' });
          throw uploadError;
        }
      }
      nextCheckAt = Date.now() + 15_000;
    } catch {
      nextCheckAt = Date.now() + 30_000;
    }
  })().finally(() => { checker = null; });
  return checker;
};

if (typeof registerSupportDiagnosticSyncHook === 'function') {
  registerSupportDiagnosticSyncHook(status => {
    void enqueueLegacySupportCheck(() => checkPendingSupportDiagnosticRequests(`sync_${status}`));
  });
}

const syncDiagnosticStorage = (scope: Pick<Scope, 'companyId' | 'userId' | 'deviceId'>) => new MMKV({
  id: `app-guincho-sync-diagnostic:${scope.companyId}:${scope.userId}:${scope.deviceId}`,
});

registerSyncDiagnosticListener(result => {
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  if (!user || user.id !== result.userId || profile?.id !== user.id ||
      profile.empresa_id !== result.companyId || identityKey !== `${result.companyId}:${result.userId}`) return;
  const scope = { companyId: result.companyId, userId: result.userId, deviceId: getStableRecoveryDeviceId() };
  const events = getBufferedLogEvents();
  const essential = getSyncEssentialEvents();
  const outcome = { ...result, networkConnected: result.networkConnected ?? globalState.network.isConnected.get(),
    ...classifySyncDiagnosticFailure(result, [...events, ...essential]),
    stage: result.stage ?? 'sync_all' };
  const capturedEvents = mergeSyncDiagnosticEvents(events, essential, result.sqliteOperations);
  const capture = () => {
    if (identityKey !== `${scope.companyId}:${scope.userId}`) return;
    const journal = new SyncFailureJournal(syncDiagnosticStorage(scope));
    const recorded = journal.record(scope, outcome, capturedEvents, uuidv4, { platform: Platform.OS, clientBuild: getRuntimeClientBuild() });
    if (result.phase !== 'failure') {
      journal.finish(scope, outcome);
      if (recorded || journal.read(scope)) setTimeout(() => { void checkPendingSupportDiagnosticRequests('sync_failure'); }, 0);
    }
  };
  if (Platform.OS !== 'web') { capture(); return; }
  // Capturar o buffer antes do lock; outra aba não pode substituir a ocorrência já persistida.
  if (typeof navigator !== 'undefined' && navigator.locks) {
    void navigator.locks.request(`app-guincho-sync-capture:${scope.companyId}:${scope.userId}:${scope.deviceId}`,
      capture).catch(() => undefined);
  }
});

const sendAutomaticSyncDiagnostic = async (scope: Scope): Promise<void> => {
  if (hasSyncExecution()) return;
  const send = async () => {
    const storage = syncDiagnosticStorage(scope);
    const journal = new SyncFailureJournal(storage);
    const incident = journal.read(scope);
    if (!incident) return;
    const store = new MMKVSupportDiagnosticPreparedStore(storage);
    const buffer = new SupportDiagnosticBuffer({ maxBytes: 1024 * 1024, previousStats: incident.stats });
    let prepared = await store.loadLatest();
    if (prepared && Date.now() - Date.parse(prepared.createdAt) >= SYNC_DIAGNOSTIC_TTL_MS) {
      store.clear(); prepared = null;
    }
    if (!prepared) {
      incident.events.forEach(event => buffer.record(event));
      const preparer = new SupportDiagnosticService({ buffer, store, createId: () => incident.diagnosticId,
        transport: { uploadExplicit: async () => { throw new Error('prepare_only'); } } });
      prepared = await preparer.prepareForSystemAutomatic({ metadata: { ...scope, category: 'sync',
        incidentId: incident.diagnosticId, firstOccurrenceAt: incident.createdAt, lastOccurrenceAt: incident.createdAt,
        occurrenceCount: 1, platform: (incident.metadata as any)?.platform,
        clientBuild: (incident.metadata as any)?.clientBuild,
        errorCode: (incident.metadata as any)?.errorCode, stage: (incident.metadata as any)?.stage } });
    }
    const metadata = (prepared.snapshot.metadata as any)?.context;
    if ((prepared.snapshot.metadata as any)?.delivery_basis?.kind !== 'system_automatic' || metadata?.category !== 'sync' ||
        metadata.companyId !== scope.companyId || metadata.userId !== scope.userId || metadata.deviceId !== scope.deviceId ||
        metadata.incidentId !== incident.diagnosticId) { store.clear(); return; }
    const context = { category: 'sync', first_occurrence_at: metadata.firstOccurrenceAt,
      last_occurrence_at: metadata.lastOccurrenceAt, occurrence_count: 1, platform: metadata.platform };
    const prepare = (p_context: Record<string, unknown>) => timeout(supabase.rpc('app_guincho_prepare_automatic_support_diagnostic_v1', {
      p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: metadata.clientBuild,
      p_request_id: prepared!.diagnosticId, p_protocol: PROTOCOL, p_context,
    }));
    const rpcStage = metadata.stage === 'push_local_changes' ? 'push' : metadata.stage ?? 'sync_all';
    let { data, error } = await prepare({ ...context,
      trigger: metadata.errorCode ?? 'sync_failed', stage: rpcStage });
    // Compatibilidade com backend anterior ou pedido legado já criado, sem trocar ID ou identidade.
    if (error?.message === 'automatic_diagnostic_context_invalid' || error?.message === 'automatic_diagnostic_identity_conflict') {
      ({ data, error } = await prepare(context));
    }
    if (error || first(data as { request_id: string }[] | null)?.request_id !== prepared.diagnosticId) {
      throw error ?? new Error('automatic_sync_ticket_failed');
    }
    if (identityKey !== `${scope.companyId}:${scope.userId}`) return;
    const service = new SupportDiagnosticService({ buffer, store, transport: new SupabaseSupportDiagnosticTransport(storageClient, {
      ...scope, clientBuild: metadata.clientBuild, requestId: prepared.diagnosticId,
      kind: 'system_automatic', eventCount: prepared.snapshot.events.length,
    }) });
    await service.sendSystemAutomatic(prepared.diagnosticId);
    journal.acknowledge(scope, incident.diagnosticId);
    await service.discardPrepared(prepared.diagnosticId);
  };
  if (Platform.OS !== 'web') return send();
  if (typeof navigator !== 'undefined' && navigator.locks) {
    await navigator.locks.request(`app-guincho-sync-send:${scope.companyId}:${scope.userId}:${scope.deviceId}`,
      { ifAvailable: true }, async lock => { if (lock) await send(); });
  }
};
